import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface IMultiStepFormContext<T = object> {
  currentStep: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  reset: () => void;
  data: T;
  setData: (patch: Partial<T>) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MultiStepFormContext = createContext<IMultiStepFormContext<any> | null>(null);

interface IProps {
  totalSteps: number;
}

export const MultiStepFormProvider = <T extends object>({
  totalSteps,
  children,
}: PropsWithChildren<IProps>) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setDataState] = useState<T>({} as T);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
      }
    },
    [totalSteps],
  );

  const setData = useCallback((patch: Partial<T>) => {
    setDataState(prev => ({ ...prev, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setDataState({} as T);
  }, []);

  const value = useMemo(
    () => ({
      currentStep,
      totalSteps,
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === totalSteps - 1,
      nextStep,
      prevStep,
      goToStep,
      reset,
      data,
      setData,
    }),
    [currentStep, totalSteps, nextStep, prevStep, goToStep, reset, data, setData],
  );

  return <MultiStepFormContext.Provider value={value}>{children}</MultiStepFormContext.Provider>;
};

export const useMultiStepForm = <T extends object = object>() => {
  const context = useContext(MultiStepFormContext);

  if (!context) {
    throw new Error('useMultiStepForm must be used within a MultiStepFormProvider');
  }

  return context as IMultiStepFormContext<T>;
};
